import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Navigate, useParams, useNavigate, Link } from 'react-router-dom';
import { collection, query, onSnapshot, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import OverviewDashboard from '../../components/admin/OverviewDashboard';
import BarbersManagement from '../../components/admin/BarbersManagement';
import ServicesManagement from '../../components/admin/ServicesManagement';
import VIPManagement from '../../components/admin/VIPManagement';
import SystemConfiguration from '../../components/admin/SystemConfiguration';
import AnalyticsDashboard from '../../components/admin/AnalyticsDashboard';
import { LoyaltyConfigPanel } from '../../components/admin/LoyaltyConfigPanel';
import { VIPApprovalPanel } from '../../components/admin/VIPApprovalPanel';
import { PointsManagementPanel } from '../../components/admin/PointsManagementPanel';
import ClientsManagement from '../../components/admin/ClientsManagement';
import CommissionsPanel from '../../components/admin/CommissionsPanel';
import { BannerConfigPanel } from '../../components/admin/BannerConfigPanel';

interface Franchise {
  franchiseId: string;
  name: string;
}

interface Branch {
  branchId: string;
  name: string;
  franchiseId: string;
}

type TabType = 'overview' | 'barbers' | 'services' | 'clients' | 'vip' | 'loyalty' | 'points' | 'commissions' | 'banner' | 'config' | 'analytics';

interface Tab {
  id: TabType;
  label: string;
  icon: string;
}

const tabs: Tab[] = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'barbers', label: 'Barberos', icon: '✂️' },
  { id: 'services', label: 'Servicios', icon: '💈' },
  { id: 'clients', label: 'Clientes', icon: '👥' },
  { id: 'vip', label: 'Gestión VIP', icon: '👑' },
  { id: 'loyalty', label: 'Recompensas', icon: '🎁' },
  { id: 'points', label: 'Gestión Puntos', icon: '✨' },
  { id: 'commissions', label: 'Comisiones', icon: '💰' },
  { id: 'banner', label: 'Banner TV', icon: '📺' },
  { id: 'config', label: 'Configuración', icon: '⚙️' },
  { id: 'analytics', label: 'Analíticas', icon: '📈' },
];

const AdminPanel: React.FC = () => {
  const { user, loading } = useAuth();
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();

  // Get active tab from URL, default to 'overview'
  const activeTab = (section as TabType) || 'overview';

  // Redirect to /admin/overview if on /admin
  useEffect(() => {
    if (!section) {
      navigate('/admin/overview', { replace: true });
    }
  }, [section, navigate]);

  const [selectedFranchiseId, setSelectedFranchiseId] = useState<string>('all');
  const [selectedBranchId, setSelectedBranchId] = useState<string>('all');
  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);

  // Track if franchise change was triggered by branch selection
  const isAutoSelectingFranchise = useRef(false);

  // Load franchises
  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(db, 'franchises')), (snapshot) => {
      const franchiseList = snapshot.docs.map((doc) => ({
        franchiseId: doc.id,
        name: doc.data().name,
      })) as Franchise[];
      setFranchises(franchiseList);
    });

    return () => unsubscribe();
  }, []);

  // Load branches based on selected franchise
  useEffect(() => {
    if (selectedFranchiseId === 'all') {
      const unsubscribe = onSnapshot(query(collection(db, 'branches')), (snapshot) => {
        const branchList = snapshot.docs.map((doc) => ({
          branchId: doc.id,
          name: doc.data().name,
          franchiseId: doc.data().franchiseId,
        })) as Branch[];
        setBranches(branchList);
      });
      return () => unsubscribe();
    } else {
      const unsubscribe = onSnapshot(
        query(collection(db, 'branches'), where('franchiseId', '==', selectedFranchiseId)),
        (snapshot) => {
          const branchList = snapshot.docs.map((doc) => ({
            branchId: doc.id,
            name: doc.data().name,
            franchiseId: doc.data().franchiseId,
          })) as Branch[];
          setBranches(branchList);
        }
      );
      return () => unsubscribe();
    }
  }, [selectedFranchiseId]);

  // Auto-select franchise when branch is selected
  useEffect(() => {
    if (selectedBranchId !== 'all') {
      // Find the franchise for this branch
      const branch = branches.find(b => b.branchId === selectedBranchId);
      if (branch && branch.franchiseId !== selectedFranchiseId) {
        isAutoSelectingFranchise.current = true;
        setSelectedFranchiseId(branch.franchiseId);
      }
    }
  }, [selectedBranchId, branches, selectedFranchiseId]);

  // Reset branch selection when franchise changes (but only if it was a manual change)
  useEffect(() => {
    if (isAutoSelectingFranchise.current) {
      // This was an auto-select, don't reset the branch
      isAutoSelectingFranchise.current = false;
    } else {
      // This was a manual franchise change, reset the branch
      setSelectedBranchId('all');
    }
  }, [selectedFranchiseId]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-canvas">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-ink-muted">Loading…</p>
        </div>
      </div>
    );
  }

  // Check if user is super_admin
  if (!user || user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  const renderActiveTab = () => {
    const filters = { selectedFranchiseId, selectedBranchId };

    switch (activeTab) {
      case 'overview':
        return <OverviewDashboard {...filters} />;
      case 'barbers':
        return <BarbersManagement {...filters} />;
      case 'services':
        return <ServicesManagement {...filters} />;
      case 'clients':
        return <ClientsManagement {...filters} />;
      case 'vip':
        return (
          <div className="space-y-6">
            <VIPManagement {...filters} />
            <VIPApprovalPanel {...filters} />
          </div>
        );
      case 'loyalty':
        return <LoyaltyConfigPanel selectedFranchiseId={selectedFranchiseId} />;
      case 'points':
        return <PointsManagementPanel {...filters} />;
      case 'commissions':
        return <CommissionsPanel {...filters} />;
      case 'banner':
        return <BannerConfigPanel selectedFranchiseId={selectedFranchiseId} />;
      case 'config':
        return <SystemConfiguration />;
      case 'analytics':
        return <AnalyticsDashboard {...filters} />;
      default:
        return <OverviewDashboard {...filters} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-surface border border-hairline rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-display text-2xl text-ink">Admin Panel</h1>
            <p className="text-sm text-ink-muted mt-1">
              Welcome back, {user.displayName}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-ink">{user.displayName}</p>
              <p className="text-xs text-ink-subtle">{user.role}</p>
            </div>
            {user.photoURL && (
              <img
                src={user.photoURL}
                alt={user.displayName}
                className="h-10 w-10 rounded-full ring-1 ring-hairline"
              />
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 pt-4 border-t border-hairline">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-ink-muted">
              Franquicia:
            </label>
            <select
              value={selectedFranchiseId}
              onChange={(e) => setSelectedFranchiseId(e.target.value)}
              className="px-3 py-2 border border-hairline rounded-lg bg-surface-3 text-ink text-sm focus:ring-2 focus:ring-focus focus:border-transparent"
            >
              <option value="all">Todas las franquicias</option>
              {franchises.map((franchise) => (
                <option key={franchise.franchiseId} value={franchise.franchiseId}>
                  {franchise.name}
                </option>
              ))}
            </select>
          </div>

          {/* Hide branch filter for Clients tab - clients belong to franchise, not branch */}
          {activeTab !== 'clients' && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-ink-muted">
                Sucursal:
              </label>
              <select
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                disabled={selectedFranchiseId === 'all' && branches.length === 0}
                className="px-3 py-2 border border-hairline rounded-lg bg-surface-3 text-ink text-sm focus:ring-2 focus:ring-focus focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="all">Todas las sucursales</option>
                {branches.map((branch) => (
                  <option key={branch.branchId} value={branch.branchId}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(selectedFranchiseId !== 'all' || (selectedBranchId !== 'all' && activeTab !== 'clients')) && (
            <button
              onClick={() => {
                setSelectedFranchiseId('all');
                setSelectedBranchId('all');
              }}
              className="text-sm text-accent hover:underline font-medium"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-surface border border-hairline rounded-xl">
        <nav className="flex space-x-8 overflow-x-auto px-6" aria-label="Tabs">
          {tabs.map((tab) => (
            <Link
              key={tab.id}
              to={`/admin/${tab.id}`}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                transition-colors duration-200 flex items-center space-x-2
                ${
                  activeTab === tab.id
                    ? 'border-accent text-accent'
                    : 'border-transparent text-ink-subtle hover:text-ink hover:border-hairline-strong'
                }
              `}
            >
              <span className="text-lg">{tab.icon}</span>
              <span>{tab.label}</span>
            </Link>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="bg-surface border border-hairline rounded-xl min-h-[600px]">
        {renderActiveTab()}
      </div>
    </div>
  );
};

export default AdminPanel;
